import { useState } from 'react';
import { ArrowLeft, Plus, Download, Upload, Edit, Trash2, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguagePacks, CreateLanguagePackData } from '@/hooks/useLanguagePacks';
import { toast } from 'sonner';

const LanguagePacks = () => {
  const { t } = useTranslation();
  const { 
    languagePacks, 
    isLoading, 
    createLanguagePack, 
    deleteLanguagePack, 
    exportLanguagePack,
    isCreating,
    isDeleting,
    isExporting
  } = useLanguagePacks();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newPackData, setNewPackData] = useState<CreateLanguagePackData>({
    language_code: '',
    language_name: '',
  });

  const handleCreatePack = () => {
    if (!newPackData.language_code || !newPackData.language_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    createLanguagePack(newPackData);
    setIsCreateDialogOpen(false);
    setNewPackData({ language_code: '', language_name: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'incomplete':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'draft':
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">{t('languagePacks.title')}</h1>
              <p className="text-muted-foreground">{t('languagePacks.subtitle')}</p>
            </div>
            <div className="flex gap-2">
              <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="cta-secondary">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('languagePacks.create')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="glass-panel border-0">
                  <DialogHeader>
                    <DialogTitle>{t('languagePacks.create')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="language_code">Language Code</Label>
                      <Input
                        id="language_code"
                        value={newPackData.language_code}
                        onChange={(e) => setNewPackData({ ...newPackData, language_code: e.target.value })}
                        placeholder="e.g., es, it, pt"
                        className="glass-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="language_name">Language Name</Label>
                      <Input
                        id="language_name"
                        value={newPackData.language_name}
                        onChange={(e) => setNewPackData({ ...newPackData, language_name: e.target.value })}
                        placeholder="e.g., Spanish, Italian, Portuguese"
                        className="glass-input"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setIsCreateDialogOpen(false)}
                        className="glass-button"
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button 
                        onClick={handleCreatePack}
                        disabled={isCreating}
                        className="cta-primary"
                      >
                        {isCreating ? t('common.loading') : t('common.create')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {languagePacks.map((pack) => (
            <Card key={pack.id} className="glass-panel border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    {pack.language_name}
                  </CardTitle>
                  <Badge className={getStatusColor(pack.status)}>
                    {t(`languagePacks.status.${pack.status}`)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Code: {pack.language_code.toUpperCase()}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>{t('languagePacks.progress')}</span>
                    <span>{Math.round(pack.completion_percentage)}%</span>
                  </div>
                  <Progress value={pack.completion_percentage} className="h-2" />
                </div>
                
                <div className="text-xs text-muted-foreground">
                  <p>{t('languagePacks.lastUpdated')}: {new Date(pack.updated_at).toLocaleDateString()}</p>
                  {pack.is_system_default && (
                    <p className="text-primary">System Default</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="glass-button flex-1"
                    onClick={() => exportLanguagePack(pack.id)}
                    disabled={isExporting}
                  >
                    <Download className="h-3 w-3 mr-1" />
                    {t('common.export')}
                  </Button>
                  {!pack.is_system_default && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="glass-button"
                      onClick={() => deleteLanguagePack(pack.id)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {languagePacks.length === 0 && (
          <div className="text-center py-12">
            <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Language Packs</h3>
            <p className="text-muted-foreground mb-4">
              Create your first language pack to start managing translations.
            </p>
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="cta-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('languagePacks.create')}
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguagePacks;